import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useAsyncFn } from 'react-use';
import AppTable from 'components/AppTable';
import ModalBusyContainer from 'components/ModalBusyContainer';
import withTable from 'HOC/withTable';
import withTableProps from 'HOC/types/withTable';
import TableFilterToolbar from 'components/TableFilterToolbar';
import StatisticsInfo from 'components/StatisticsInfo';
import PageLayout from 'layouts/PageLayout';
import Donut from 'components/Donut';
import { makeStyles } from '@material-ui/core/styles';
import { toggleFilterArrayValue, transformTableData, transformToComplianceStatusBuckets } from 'utils';
import { useSelector } from 'react-redux';
import { exportDashboardStatistics, getDashboardStatistics } from 'http/statistics';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { routesAliases } from 'router/routesAliases';
import getTableConfig from './helpers/getTableConfig';

const pageAlias = 'Dashboard';

const useStyles = makeStyles(theme => ({
  mainContent: {
    display: 'flex',
    marginTop: 30,
  },
  mainContentLeft: {
    paddingTop: 30,
    flexGrow: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  mainContentRight: {
    flexGrow: 1,
  },
  pageWrapper: {
    padding: theme.spacing(1, 4),
  },
  donutTitle: {
    marginBottom: 20,
  },
  donutRoot: {
    flexDirection: 'column-reverse',
    alignItems: 'center',
  },
  helperText: {
    margin: '8px 0 0',
    maxWidth: 430,
    color: theme.palette.text.grey,
    lineHeight: 1.5,
    fontSize: '0.9rem',
  },
}));

const Dashboard = ({ tableData, total, graphic }) => {
  const classes = useStyles();
  const history = useHistory();
  const { lang } = useSelector(state => state.auth);
  const { t } = useTranslation('common');
  const tableConfig = getTableConfig();
  const [donutData, setDonutData] = useState([]);
  const [statisticsInfo, setStatisticsInfo] = useState([]);
  const jobTaskLabelsRef = useRef({});
  const [columns, dashboardTableData] = transformTableData(tableConfig, tableData.data);
  const [exportState, exportDashboardFile] = useAsyncFn(async () => {
    await exportDashboardStatistics(tableData.getRequestParams({ includePagination: false }));
  }, [tableData]);

  const handleComplianceFilter = complianceStatus => {
    if (!complianceStatus) {
      return;
    }

    const nextValue = toggleFilterArrayValue(tableData.filtering.complianceStatuses, complianceStatus);
    if (!nextValue.length) {
      tableData.handleRemoveFilter('complianceStatuses');
      return;
    }

    tableData.handleFiltering('complianceStatuses', nextValue);
  };

  useEffect(() => {
    tableData.handleChangeFetchMethod(pageAlias, getDashboardStatistics);
  }, [lang]);

  useEffect(() => {
    setStatisticsInfo([
      {
        title: t('dashboard.totalSessions'),
        value: total.sessions,
        subtitle: t('dashboard.subtitleSessions'),
      },
      {
        title: t('dashboard.totalStudents'),
        value: total.students,
        subtitle: t('dashboard.subtitlePeople'),
      },
      {
        title: t('dashboard.totalTrainers'),
        value: total.trainers,
        subtitle: t('dashboard.subtitlePeople'),
      },
    ]);
  }, [total, t]);

  useEffect(() => {
    setDonutData(transformToComplianceStatusBuckets(graphic));
  }, [graphic]);

  useEffect(() => {
    (tableData.filterOptions.jobTasks || []).forEach(option => {
      jobTaskLabelsRef.current[String(option.value)] = option.label;
    });
  }, [tableData.filterOptions.jobTasks]);

  const jobTaskOptions = useMemo(() => {
    const backendOptions = tableData.filterOptions.jobTasks || [];
    const selectedValues = Array.isArray(tableData.filtering.jobTaskIds) ? tableData.filtering.jobTaskIds : [];
    const optionsByValue = new Map(backendOptions.map(option => [String(option.value), option]));

    selectedValues.forEach(value => {
      const key = String(value);
      if (!optionsByValue.has(key) && jobTaskLabelsRef.current[key]) {
        optionsByValue.set(key, { value, label: jobTaskLabelsRef.current[key] });
      }
    });

    return Array.from(optionsByValue.values());
  }, [tableData.filterOptions.jobTasks, tableData.filtering.jobTaskIds]);

  const complianceStatusOptions = useMemo(() => {
    const backendOptions = tableData.filterOptions.complianceStatuses || [];
    return backendOptions.map(option => ({
      ...option,
      label: t(`compliance.statuses.${option.value}`, { defaultValue: option.label }),
    }));
  }, [tableData.filterOptions.complianceStatuses, t]);

  return (
    <PageLayout
      pageTitle={t('dashboard.pageTitle')}
      contentHeader={t('dashboard.trainingRequirementsOverview')}
      actionSectionLeft={[
        {
          type: 'component',
          Component: <StatisticsInfo items={statisticsInfo} />,
        },
      ]}
      actionSectionRight={[
        {
          type: 'button',
          icon: 'add',
          title: t('dashboard.actionAddStudent'),
          onClick: () => history.push(routesAliases.createStudent),
        },
        {
          type: 'button',
          icon: 'add',
          title: t('dashboard.actionAddTrainer'),
          onClick: () => history.push(routesAliases.createTrainer),
        },
        {
          type: 'button',
          icon: 'import_export',
          title: t('commonActions.actionExport'),
          onClick: exportDashboardFile,
          disabled: exportState.loading,
        },
      ]}
      pageContentActions={[
        {
          type: 'component',
          Component: (
            <TableFilterToolbar
              filters={[
                {
                  type: 'search',
                  name: 'search',
                  label: t('tableFilters.search'),
                  placeholder: t('placeholders.searchStudent'),
                },
                {
                  type: 'multiSelect',
                  name: 'complianceStatuses',
                  label: t('complianceStatus'),
                  options: complianceStatusOptions,
                },
                {
                  type: 'multiSelect',
                  name: 'jobTaskIds',
                  label: t('tablesLabels.jobTask'),
                  options: jobTaskOptions,
                },
              ]}
              filtering={tableData.filtering}
              onFilterChange={tableData.handleFiltering}
              onRemoveFilter={tableData.handleRemoveFilter}
              onClearFilters={tableData.handleClearFilters}
            />
          ),
        },
      ]}
      classes={{
        content: classes.pageWrapper,
      }}
    >
      <div className={classes.mainContent}>
        {donutData && !!donutData.length && (
          <div className={classes.mainContentLeft}>
            <Donut
              donutWidth={430}
              donutHeight={430}
              donutData={donutData}
              donutTitle={t('legend.trainingRequirementsByComplianceStatus')}
              donutTitleStyles={classes.donutTitle}
              onDatumClick={handleComplianceFilter}
              classes={{
                root: classes.donutRoot,
              }}
            />
            <p className={classes.helperText}>{t('dashboard.complianceWidgetHelp')}</p>
          </div>
        )}
        <div className={classes.mainContentRight}>
          <ModalBusyContainer busy={tableData.isFetchingTableData || exportState.loading}>
            <AppTable
              columns={columns}
              data={dashboardTableData}
              totalCount={tableData.totalCount}
              rowsPerPage={tableData.pageSize}
              onChangePage={tableData.handleChangePage}
              currentPage={tableData.page}
              onColumnSorting={tableData.handleSorting}
              systemField="userId"
              extraActionsKeys={['userId']}
              isLazyLoading
              actions={[
                {
                  title: t('buttons.view'),
                  onClick: event =>
                    history.push({
                      pathname: `${routesAliases.editStudent}/${event.currentTarget.dataset.userid}`,
                    }),
                },
              ]}
            />
          </ModalBusyContainer>
        </div>
      </div>
    </PageLayout>
  );
};

Dashboard.defaultProps = {
  total: {},
  graphic: {},
};

Dashboard.propTypes = {
  tableData: PropTypes.shape(withTableProps).isRequired,
  total: PropTypes.shape(),
  graphic: PropTypes.shape(),
};

export default withTable({
  defaultSorting: {},
  fetchingData: 'items',
  pageAlias,
  allowedFilters: ['search', 'jobTaskIds', 'complianceStatuses'],
})(Dashboard);
